<template>
  <transition name="cookie-slide">
    <div
      v-if="visible"
      class="cookie-banner"
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
    >
      <h2 class="cookie-banner__title">We value your privacy</h2>

      <p class="cookie-banner__text">
        We use cookies to run this website, to improve your experience, and to
        measure how the site is used. You can accept all cookies or reject the
        ones that are not essential. Read our
        <a href="/privacy-policy" class="cookie-banner__link">Privacy Policy</a>.
      </p>

      <div class="cookie-banner__actions">
        <button
          type="button"
          class="cookie-btn cookie-btn--reject"
          @click="reject"
        >
          Reject
        </button>
        <button
          type="button"
          class="cookie-btn cookie-btn--accept"
          @click="acceptAll"
        >
          Accept all
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import { eventBus } from "@/utils/eventBus";
import { setConsent, hasResponded } from "@/utils/consent";
import { syncConsent } from "@/utils/trackers";

/**
 * CookieBanner
 *
 * Bare minimum UK PECR / UK GDPR consent banner. It shows until the visitor makes
 * a choice, remembers that choice (first party cookie via consent.js), and only
 * loads the non-essential tags on Accept (trackers.js). It can be reopened from
 * the footer "Cookie settings" link, and Reject is exactly as easy as Accept.
 *
 * Tracker: docs/plans/cookie-banner-implementation.md
 */
export default {
  name: "CookieBanner",
  data() {
    return {
      visible: false
    };
  },
  created() {
    // Only show until the visitor has made a choice.
    if (!hasResponded()) this.open();
    // Footer "Cookie settings" link reopens the banner so a visitor can change
    // their choice (withdrawal as easy as consent, per UK PECR).
    eventBus.$on("open-cookie-banner", this.open);
  },
  beforeDestroy() {
    eventBus.$off("open-cookie-banner", this.open);
  },
  methods: {
    open() {
      this.visible = true;
    },
    close() {
      this.visible = false;
    },
    acceptAll() {
      this.handleChoice("all");
    },
    reject() {
      this.handleChoice("essential");
    },
    handleChoice(choice) {
      const acceptAll = choice === "all";
      // Remember the choice so tags stay gated on return visits.
      setConsent({ analytics: acceptAll, marketing: acceptAll });
      this.$emit("choice", choice);
      this.close();
      // Load newly granted tags, stop any that were just withdrawn, and tell
      // consent-aware components to refresh. On a plain Reject nothing
      // non-essential loads; on a withdrawal (Accept then Reject) syncConsent
      // clears the pixel cookies and reloads so tracking actually stops.
      syncConsent();
    }
  }
};
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 9999;
  width: 460px;
  max-width: calc(100vw - 48px);
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 8px 30px rgba(0, 36, 88, 0.18);
  padding: 22px 24px;
  box-sizing: border-box;
}

.cookie-banner__title {
  font-family: "Sora", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #002458;
  margin: 0 0 8px;
  line-height: 1.3;
}

.cookie-banner__text {
  font-size: 14px;
  color: #4a4a4a;
  line-height: 1.55;
  margin: 0 0 18px;
}

.cookie-banner__link {
  color: #08a0a8;
  font-weight: 600;
  text-decoration: none;
}

.cookie-banner__link:hover {
  text-decoration: underline;
  opacity: 1;
}

.cookie-banner__actions {
  display: flex;
  gap: 12px;
}

/* Both buttons share identical size and weight so Reject is exactly as easy
   to use as Accept (UK PECR / ICO guidance). */
.cookie-btn {
  flex: 1 1 0;
  font-family: "Sora", sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 11px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.cookie-btn--accept {
  background: #08a0a8;
  color: #ffffff;
  border: 1.5px solid #08a0a8;
}

.cookie-btn--accept:hover {
  background: #078289;
  border-color: #078289;
}

.cookie-btn--reject {
  background: #ffffff;
  color: #002458;
  border: 1.5px solid #002458;
}

.cookie-btn--reject:hover {
  background: #f3f4f6;
}

.cookie-btn:focus-visible {
  outline: 2px solid #002458;
  outline-offset: 2px;
}

/* Slide up + fade in. */
.cookie-slide-enter-active,
.cookie-slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.cookie-slide-enter,
.cookie-slide-leave-to {
  transform: translateY(16px);
  opacity: 0;
}

@media (max-width: 600px) {
  .cookie-banner {
    left: 12px;
    right: 12px;
    bottom: 12px;
    width: auto;
    max-width: none;
  }

  .cookie-banner__actions {
    flex-direction: column;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cookie-slide-enter-active,
  .cookie-slide-leave-active {
    transition: opacity 0.2s ease;
  }

  .cookie-slide-enter,
  .cookie-slide-leave-to {
    transform: none;
  }
}
</style>
